import React, { useEffect, useMemo, useState } from 'react'
import { map, path, pathOr } from 'ramda'
import { subscribe } from 'react-contextual'
import { stringUtils } from '../../../../utils'
import { SearchFilterProvider } from '../../../../views/providers'
import { AggregateTable, Button } from '../../../../views/components'
import { ContactsProvider } from '../..'
import Name from './Name'
import Status from './Status'
import styles from './ContactsTable.module.scss'
import {
  applyContactFilterPills,
  getContactStatusLabel,
  getOutstandingLabels,
  hasOutstandingColumn,
  hasStatusColumn,
} from '../../filters'
import { getLiveAccount } from '../../status'
import { getContactTagLabels, hasTagsColumn } from '../../tags'
import { useContactFilterPills } from '../../useContactFilterPills'
import { sortByProps } from 'utils/array'
import { $TSFixMe } from 'types/ts-migrate'
import { get } from 'lodash-es'
import { useSelector } from 'react-redux'
import { portfolioApiSelectors } from 'modules/api/portfolio'

const nameColumn = {
  Header: 'Name',
  accessor: 'name',
  style: {
    minWidth: '400px',
    textAlign: 'left',
    justifyContent: 'flex-start',
    paddingLeft: '16px',
  },
  sort: {
    enabled: true,
    // Every tab lands sorted A-Z by name until the user sorts on something else.
    order: 'asc',
  },
}

const unsortedNameColumn = { ...nameColumn, sort: { enabled: true, order: false } }

const statusColumn = {
  Header: 'Status',
  accessor: 'status',
  style: {
    maxWidth: '150px',
    textAlign: 'left',
    justifyContent: 'flex-start',
    paddingLeft: '16px',
  },
  sort: {
    enabled: true,
    order: 'asc',
  },
}

const outstandingColumn = {
  Header: 'Needs attention',
  accessor: 'outstanding',
  style: {
    justifyContent: 'flex-end',
  },
}

const tagsColumn = {
  Header: '',
  accessor: 'tags',
  style: {
    justifyContent: 'flex-end',
  },
}

const contactsColumn = {
  Header: 'Contact Number',
  accessor: 'contactNumber',
  style: {
    maxWidth: '150px',
  },
  sort: {
    enabled: true,
    order: false,
  },
}

const emailColumn = {
  Header: 'Email',
  accessor: 'email',
  style: {
    maxWidth: '250px',
  },
  sort: {
    enabled: true,
    order: false,
  },
}

const getTagModifier = (label: string): $TSFixMe => {
  switch (label) {
    case 'Tenant':
      return { tertiary: true }
    case 'Owner':
      return { secondary: true }
    default:
      return { primary: true }
  }
}

const ContactsTable = ({
  filters,
  openContact,
  primaryPanel,
  searchQuery,
  activeFilterIndex,
  currentParty,
}: any): React.ReactElement => {
  const [data, setData] = useState<$TSFixMe[]>([])
  const filter: $TSFixMe = pathOr({}, [activeFilterIndex], filters)
  const tabKey: string = pathOr('', ['key'], filter)
  const { selection } = useContactFilterPills()
  // Keyed to everyone on the tab, not to the filtered rows, so the column does not appear and
  // disappear as the pills are toggled.
  const showOutstanding = hasOutstandingColumn(tabKey, filter.contacts ?? [])
  const showStatus = hasStatusColumn(tabKey)
  const showTags = hasTagsColumn(tabKey)

  useEffect(() => {
    setData(
      applyContactFilterPills(filter.contacts ?? [], selection, { tabKey }).map((contact: $TSFixMe) => {
        const statusLabel = getContactStatusLabel(contact, tabKey)

        return {
          ...contact,
          meta: {
            name: contact?.name,
            contactNumber: contact?.contactNumber,
            email: contact?.email,
            // Active first, Inactive after, each A-Z. Empty on a tab without the column
            status: statusLabel ? `${statusLabel}|${contact?.name ?? ''}` : '',
          },
        }
      }),
    )
  }, [filter.contacts, selection, tabKey])

  const filteredContacts = useMemo(() => {
    return data.filter(inv => stringUtils.jsonStringSearch(searchQuery, inv))
  }, [searchQuery, data])

  const isPrimaryPanelOpen = Boolean(path(['open'], primaryPanel))

  const columns = useMemo(
    () =>
      isPrimaryPanelOpen
        ? [nameColumn, emailColumn]
        : [
            ...(showStatus ? [statusColumn] : []),
            showStatus ? unsortedNameColumn : nameColumn,
            emailColumn,
            contactsColumn,
            ...(showOutstanding ? [outstandingColumn] : []),
            ...(showTags ? [tagsColumn] : []),
          ],
    [isPrimaryPanelOpen, showOutstanding, showStatus, showTags],
  )

  const currentPartyId = get(currentParty, 'id')

  const getPortfolioStatus = useSelector(portfolioApiSelectors.getSummaryStatusByPortfolioId)

  const contactsData = useMemo(() => {
    // Only a live lease earns a line under the name — the same lease that makes the contact Active.
    const getNameMeta = (accounts: $TSFixMe[]): $TSFixMe => {
      const activeAccount = getLiveAccount(accounts, getPortfolioStatus)

      return activeAccount ? `${activeAccount.tag as string}: ${activeAccount.propertyAddress as string}` : null
    }

    const buildContactObj = (contact: any): $TSFixMe => {
      return {
        id: contact.id,
        highlighted: currentPartyId === contact.id,
        meta: contact.meta,
        partyType: contact.partyType,
        name: <Name name={contact.name} meta={getNameMeta(contact.accounts)} />,
        lastName: contact.lastName ? contact.lastName : contact.name,
        contactNumber: contact.contactNumber,
        identification: contact.identification,
        email: contact.email,
        ...(showStatus && {
          status: (
            <Status label={getContactStatusLabel(contact, tabKey) ?? ''} isActive={Boolean(contact.hasLiveLease)} />
          ),
        }),
        ...(showOutstanding && {
          outstanding: (
            <div className={styles.outstanding}>
              {getOutstandingLabels(contact, tabKey).map((label: string) => (
                <span className={styles['outstanding-pill']} key={label}>
                  {label}
                </span>
              ))}
            </div>
          ),
        }),
        ...(showTags && {
          tags: (
            <div className={styles.tags}>
              {getContactTagLabels(contact).map((label: string) => (
                <Button key={label} {...getTagModifier(label)} pill size="sm">
                  {label}
                </Button>
              ))}
            </div>
          ),
        }),
      }
    }

    const contactsResult = searchQuery ? map(buildContactObj, filteredContacts) : map(buildContactObj, data)

    return sortByProps(['lastName', 'name'], contactsResult)
  }, [
    data,
    filteredContacts,
    searchQuery,
    showOutstanding,
    showStatus,
    showTags,
    tabKey,
    currentPartyId,
    getPortfolioStatus,
  ])

  return useMemo(
    () => (
      <div className={styles.root}>
        <AggregateTable
          key={tabKey}
          rowHeight={80}
          columns={columns}
          data={contactsData}
          onRowClick={data => openContact(data.id)}
        />
      </div>
    ),
    [columns, contactsData, openContact, tabKey],
  )
}

export default subscribe(
  [ContactsProvider, SearchFilterProvider],
  ({ filters, openContact, primaryPanel, currentParty }: any, { searchQuery, activeFilterIndex }: any) => ({
    filters,
    openContact,
    primaryPanel,
    currentParty,
    searchQuery,
    activeFilterIndex,
  }),
)(ContactsTable)
